import Vue from 'vue'
import Vuex from 'vuex'
import { vuexfireMutations, firebaseAction } from 'vuexfire'
import { isEmpty } from 'lodash'
import L from 'leaflet'

import { db } from '../db'

import directionsModule from '@/store/modules/directions'
import eventsModule from '@/store/modules/events'
import departmentsModule from '@/store/modules/transit'
import transitModule from '@/store/modules/departments'
import authModule from '@/store/modules/auth'

import EventsService from '@/services/EventsService'
import PolygonsService from '@/services/PolygonsService'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    isDatabaseUp: false,

    // Contains all locations pulled from the server
    locations: [],

    // Contains all campuses pulled from the server
    campuses: [],

    transitStops: [],

    categories: [],

    tours: [],

    // Contains an array of active locations
    // Used when multiple are found in a search
    activeLocations: [],

    // Contains a singular selected location
    // Used to specify a single location when multiple are found in a given search
    selectedLocation: null,

    // Contains the currently selected campus
    activeCampus: null,

    // Contains the coordinates of the user's current location
    currentLocation: L.latLng(0, 0),

    // Whether fullscreen mode is currently enabled
    fullscreen: false,

    snackbar: {
      text: '',
      visibility: false,
      color: '',
      item: null,
      confirm: false,
      timeout: -1,
      cancelText: 'Cancel'
    },

    tags: [],

    filteredCategories: [],
    filteredTags: []
  },
  getters: {
    isDatabaseUp: state => state.isDatabaseUp,
    locations: state => {
      return state.locations.map(locationIn => {
        return fillLocation(state, locationIn)
      })
    },
    campuses: state => state.campuses,
    categories: state => state.categories,
    transitStops: state => state.transitStops,
    tours: state => state.tours,
    activeLocations: state => {
      return state.activeLocations.map(locationIn => {
        if (!locationIn.campus || !locationIn.category) {
          const filtered = state.locations.filter(location => location._id === locationIn._id)
          if (filtered.length === 1) return filtered[0]
        }

        return fillLocation(state, locationIn)
      })
    },
    selectedLocation: state => {
      const selectedLocation = state.selectedLocation
      if (selectedLocation && !(selectedLocation.campus && selectedLocation.category)) {
        const filtered = state.locations.filter(location => location._id === selectedLocation._id)
        if (filtered.length === 1) return filtered[0]
      }

      const filledLocation = fillLocation(state, state.selectedLocation)

      return (isEmpty(filledLocation)) ? null : filledLocation
    },
    activeCampus: state => state.activeCampus,
    currentLocation: state => state.currentLocation,
    fullscreen: state => state.fullscreen,
    snackbar: state => state.snackbar,
    tags: state => {
      return [...new Set(
        state.locations
          .filter(location => location.tags && location.tags.length)
          .flatMap(location => location.tags)
          .sort()
      )]
    },
    filteredCategories: state => state.filteredCategories,
    filteredTags: state => state.filteredTags
  },
  mutations: {
    ...vuexfireMutations,
    setActiveLocations (state, data) {
      state.activeLocations = data
    },
    setSelectedLocation (state, data) {
      state.selectedLocation = data
    },
    setCampuses (state, data) {
      state.campuses = data
    },
    setActiveCampus (state, data) {
      state.activeCampus = data
    },
    setCurrentLocation (state, data) {
      state.currentLocation = data
    },
    setFullscreen (state, data) {
      state.fullscreen = data
    },
    setSnackbar (state, data) {
      state.snackbar = data
    },
    setFilteredCategories (state, data) {
      state.filteredCategories = data
    },
    setFilteredTags (state, data) {
      state.filteredTags = data
    }
  },
  actions: {
    async setActiveLocations ({ commit }, locations) {
      locations.forEach(async location => {
        if (location.name) {
          Vue.set(location, 'spaces', await loadSpaces(location.name))
        }
      })

      commit('setActiveLocations', locations)
    },
    bindLocations: firebaseAction(({ bindFirebaseRef }) => {
      const serialize = (snapshot) => {
        const value = snapshot.val()
        value._id = snapshot.key

        return value
      }

      return bindFirebaseRef('locations', db.ref('locations'), { serialize })
    }),
    bindCategories: firebaseAction(({ bindFirebaseRef }) => {
      const serialize = (snapshot) => {
        const value = snapshot.val()
        value._id = snapshot.key

        return value
      }

      return bindFirebaseRef('categories', db.ref('categories'), { serialize })
    }),
    bindCampuses: firebaseAction(({ bindFirebaseRef }) => {
      const serialize = (snapshot) => {
        const value = snapshot.val()
        value._id = snapshot.key

        return value
      }

      return bindFirebaseRef('campuses', db.ref('campuses'), { serialize })
    }),
    bindTransitStops: firebaseAction(({ bindFirebaseRef }) => {
      const serialize = (snapshot) => {
        const value = snapshot.val()
        value._id = snapshot.key

        return value
      }

      return bindFirebaseRef('transitStops', db.ref('transitStops'), { serialize })
    }),
    bindTours: firebaseAction(({ bindFirebaseRef }) => {
      const serialize = (snapshot) => {
        const value = snapshot.val()
        value._id = snapshot.key

        return value
      }

      return bindFirebaseRef('tours', db.ref('tours'), { serialize })
    }),
    bindIsDatabaseUp: firebaseAction(({ bindFirebaseRef }) => {
      const serialize = (snapshot) => {
        const value = snapshot.val()
        return value
      }

      return bindFirebaseRef('isDatabaseUp', db.ref('.info/connected'), { serialize })
    }),
    async loadCampusBounds ({ commit, state }) {
      // Begin pulling all bounds for the campuses
      const promises = state.campuses.map(async campus => {
        return await PolygonsService.getPolygon(campus.way)
      })

      // On completion, assign to variable
      const bounds = await Promise.all(promises)

      // Include the bounds in the campus object
      const campuses = state.campuses.map((campus, index) => {
        Vue.set(campus, 'bounds', bounds[index])
        return campus
      })

      commit('setCampuses', campuses)
    }
  },
  modules: {
    directions: directionsModule,
    events: eventsModule,
    departments: departmentsModule,
    transit: transitModule,
    auth: authModule
  }
})

async function loadSpaces (name) {
  const response = await EventsService.getSpaces(name)

  if (response['r25:list'] && response['r25:list']['r25:item']) {
    const spaces = response['r25:list']['r25:item']
    return (Array.isArray(spaces)) ? spaces : [spaces]
  }

  return []
}

function fillLocation (state, locationIn) {
  const location = Object.assign({}, locationIn)

  // Attatch the associated category
  if (location.category && typeof location.category === 'string') {
    const filtered = state.categories.filter(category => {
      return category._id === location.category
    })

    if (filtered.length) {
      Vue.set(location, 'category', filtered[0])
    } else {
      Vue.set(location, 'category', null)
    }
  }

  // Attatch the associated campus
  if (location.campus && typeof location.campus === 'string') {
    const filtered = state.campuses.filter(campus => {
      return campus._id === location.campus
    })

    if (filtered.length) {
      Vue.set(location, 'campus', filtered[0])
    } else {
      Vue.set(location, 'campus', null)
    }
  }

  return location
}
